// The map scene: the layers, the wire arms and the per-frame feed that BOTH
// pages of the WebGL2 client need (R503, the parity plan of 2026-09-04,
// Phase B).
//
// The core client and an arcade match draw the same map. Before this module
// they built it twice, in two files, in two orders. `MapScene` is that one
// object: it owns the fourteen shared layers plus the per-hello site layer,
// it applies a hello, a state frame, an event burst and the three site
// frames, and it runs the per-frame feed of the pieces it owns. A page
// builds one over an engine, keeps its own layers around it, and calls it.
//
// **What the scene does NOT own.** Six layers belong to one page alone and
// stay with that page: the heat map, the seaquake, the arrival, the
// crosshair, the two hex-grid views, and the chart, which is not an engine
// layer at all. The seaquake is the one to argue about, because its layer is
// harmless at stage 0 and an arcade match has no seaquake — but the layer is
// never hidden (its tremor clock must not stop), it is fed the wire
// countdown of a season world, and its silt reads the same blocked mask the
// creatures read. A layer that one page always feeds and the other never
// feeds is that page's, so the core client keeps it.
//
// **The chart furniture is the scene's** (R548, §17). It is not one layer
// but a controller over two — `chartsea` under the beast and `chartscreen`
// over the fleets — and both slots are the map's. `MapSceneOpts.chart`
// decides whether it is built at all: an arcade match passes `ARCADE_CHART`
// and draws none of it but the pieces `chartPieces` names (R1580). Every arm below that moves the world marks the
// furniture stale with one `invalidate` call.
//
// **The hello order** (`applyHello`), which is the order `fast/main.ts` and
// `fast/feed.ts` kept before this module: `setGrid` FIRST, because the
// engine's data textures are one texel a cell and `onGrid` refits the camera
// and re-scatters the fluff — no layer may read the geometry before it. Then
// the viewer's banner and the two anchor figures, the owners, the blocked
// mask, the pocket walk, `sonar.reset()`, a FRESH site layer, the two cell
// sets, the map author's marks, the home hold, and the camera.
//
// **The state order** (`applyState`): a full frame's owners, then every flip
// with its own ring, then the fleets, the rounds, the beams, the contested
// cells and the gauges. Three caller hooks ride it — one after the owners,
// one per flip, one after the fleets — because the chart, the two hex views,
// the WAR tab and the selection are the page's and not the map's.
//
// **The frame order** (`frame`): the visibility writes, the audio listener,
// ONE `fleets.info()`, the sonar rings converted from mpx only when the
// empire frame hands over a new array, the sonar, the ping-lit snow and the
// wake. The camera shake the scene contributes is the torpedo jolt alone,
// published as `shakeX`/`shakeY` so the page keeps ONE `camera.setShake`
// call (R164).
//
// **This is a pure move.** Three statements changed place, and none of the
// three can be seen: `heatmap.setBlocked` now runs after the pocket walk
// instead of before it, the caller's `warEvent` after the gauges instead of
// before them, and the audio listener three statements later in the frame —
// nothing between the old place and the new one fires a cue.

import type { Camera } from '../render/camera';
import type { PingFront } from '../render/sonar-types';
import type { UIBeam } from '../render/control-types';
import type { GameState } from '../state';
import type { MapMarkerStyle, TerrainStyle, TorpedoStyle } from '../ui/settings';
import {
  AUTO_SCENE_LOOK, type CurrentLook, type FactionEdgeStyle, type FactionKeyStyle, type SceneLook,
} from '../render/scene-look';
import type { PoiSet } from '../render/poi-marks';
import type {
  ArcadeStructure, Event as WireEvent, Hello, SiegeInfo, Site, State,
} from '../gen/proto/aquaphobia';
import { EventKind, HeatmapKind, ObjKind, SiteKind, TorpedoKind } from '../gen/proto/aquaphobia';
import { CueGap, FightClock, burstCues, mapCue } from '../audio/triggers';
import { BLOCK_RIDGE, cellCenter, cellCenterX, cellCenterY, COLS, MPX_PER_PX, ringOneEdges, ROWS } from '../world';
import type { Engine } from './engine';
import { NullLayer, type FrameInfo, type ViewRect } from './layer';
import { DEFAULT_KNOBS, readKnobs, type Knobs, chartOnly } from './knobs';
import type { FastSpawnScene } from './layers/spawn';
import { TerrainLayer } from './layers/terrain';
import type { TerrainPlane } from '../render/terrain-features';
import { TerritoryBorders, TerritoryLayer } from './layers/territory';
import { ControlLayer } from './layers/control';
import { BeamPicture } from './beams';
import { SonarLayer } from './layers/sonar';
import { WakeLayer } from './layers/wake';
import { FleetLayer, type ReloadRound } from './layers/fleets';
import { magazineFor } from '../ui/format';
import { roundOrder } from '../ui/rounds';
import { slowRingPx } from '../render/detonation-recipe-kit';
import { FxLayer, packRound } from './layers/fx';
import { GuardLayer } from './layers/guard';
import { SignatureLayer } from './layers/signature';
import { SignatureOrder } from '../gen/proto/aquaphobia';
import { GUARD, RELOAD_LOOKS } from '../gen/presentation';
import { DamageLayer, classifyDamage } from './layers/damage';
import {
  CombatLayer, type CombatEvent, type CombatShipRow,
} from './layers/combat';
import {
  SiteLayer, type CaptureMark, type HoldSnap, type StructureState, type WardAura,
} from './layers/sites';
import { EffectLayer } from './layers/effects';
import { GatherLayers } from './layers/gather';
import { MarkerLayer } from './layers/markers';
import { FogLayer, SnowLayer } from './layers/ambience';
import { HazeLayer } from './layers/haze';
import { CreatureLayer } from './layers/creatures';
import { KrakenLayer } from './layers/kraken';
import { MonsterLayer } from './layers/monsters';
import { SurveyLayer } from './layers/survey';
import { SeaObjectLayer, type SeaLaneLeg } from './layers/seaobjects';
import { DenLayer } from './layers/dens';
import { denLeftSec, denRows, fragmentRows } from '../dens';
import { SapperWorkLayer, nearestListener } from './layers/sapperwork';
import type { FleetInfo } from '../render/fleet-info';
import { SEA_INFERENCE, type SeaHull } from '../seaobjects';
import { NO_RELAYS, laneLegs, shippingLanes } from './chart/pieces/shipping-lanes';
import { laneTracks, type ChainTrack } from '../lanechain';
import type { ChainFigures } from './layers/laneguide';
import { poiShown } from '../survey';
import { SeaObjectKind, ShipClass, type FleetState, type PointOfInterest } from '../gen/proto/aquaphobia';
import { TorpedoLayer, WAIT_BOOM, WAIT_KILL, impactIsMine } from './layers/torpedoes';
import { ChartFurniture, type ChartHost } from './chart/furniture';
import { DEFAULT_HOLD_SYMBOL, type HoldEra, type HoldSymbolId } from '../render/hold-marks';
import type { AnyPiece, ChartLineCfg, ChartReading, ChartSetting } from './chart/context';
import { waterLines } from './chart/pieces/water-lines';
import { depthTints } from './chart/pieces/depth-tints';
import { edgeSeam, shoalStipple } from './chart/pieces/depth-marks';
import { DepthLayer } from './layers/depth';
import { SpeedFieldLayer } from './layers/speedfield';
import { currentFlow, lookIndex } from './chart/pieces/current-flow';
import type { FlowGrid, FlowGridPatch } from './chart/pieces/current-field';
import { depthLookOf, type DepthLook } from '../depthlook';
import { LIGHT } from '../identity/pick';
import { fillRockBands } from './depth-rock';

/**
 * The slots the scene fills, in the order it fills them.
 *
 * `dispose` walks the same list through `engine.setLayer`, which disposes
 * what stood in each slot, so a page can build a second scene on one engine
 * (an arcade page plays several matches without a reload).
 */
export const SCENE_SLOTS = [
  'depthfloor', 'terrain', 'territory', 'deadzone', 'control', 'sonar',
  'gatherunder', 'sites', 'dens', 'seaobjects', 'holdeffects', 'gathermid', 'wake', 'fleets', 'monsters',
  'gatherover', 'sapperwork', 'signature',
  'torpedoes', 'effects', 'fx', 'guard', 'combat', 'damage', 'markers', 'fog', 'snow', 'haze', 'creatures',
  'kraken', 'survey', 'depth', 'speedfield', 'borders', 'chartsea', 'chartscreen',
] as const;

/** No first find: one shared empty list, so a frame allocates nothing. */
const NO_FINDS: readonly PointOfInterest[] = [];

/** The points of the hulls of one frame, for the detonation test (R2657). */
function seaHulls(fleets: readonly FleetState[]): SeaHull[] {
  if (fleets.length === 0) return [];
  return fleets.map((f) => ({ x: f.xMpx / MPX_PER_PX, y: f.yMpx / MPX_PER_PX }));
}

/** The roster the scene reads out of the hello, reused between hellos (R131). */
const COMBAT_ROSTER: CombatShipRow[] = [];

/** The one reused event the scene hands the gun-shot layer (R131). */
const COMBAT_EVENT: CombatEvent = {
  kind: 'hit', victim: 0, x: 0, y: 0, sourceX: 0, sourceY: 0,
  faction: 0, victimFaction: 0, actor: 0, shieldPct: -1,
};

/**
 * The six preview knobs of the gun looks, as one reused object (R131, R925).
 *
 * `setVisibility` runs every frame, so the scene must not build an object for
 * it. The knobs are read back into this one and handed on.
 */
const COMBAT_FORCED: Record<string, string> = {
  shotMelee: '', shotArtillery: '', shotSentinel: '',
  hitShield: '', hitHull: '', hitSink: '',
};

function combatForcedOf(k: Knobs): Record<string, string> {
  COMBAT_FORCED.shotMelee = k.shotmelee;
  COMBAT_FORCED.shotArtillery = k.shotartillery;
  COMBAT_FORCED.shotSentinel = k.shotsentinel;
  COMBAT_FORCED.hitShield = k.hitshield;
  COMBAT_FORCED.hitHull = k.hithull;
  COMBAT_FORCED.hitSink = k.hitsink;
  return COMBAT_FORCED;
}

/** No wavefronts this frame. One shared empty array (R131). */
const NO_FRONTS: readonly PingFront[] = [];

/** A test runs the frame with no window; the ear then stands at 0. */
const HAS_WINDOW = typeof window !== 'undefined';

/** No holds before the hello. One shared empty array (R131). */
const NO_HOLDS: readonly HoldSnap[] = [];

/** No hidden chart groups, for a page that names none. One shared empty
 *  array, because `setVisibility` runs every frame (R131). */
const NO_HIDDEN: readonly string[] = [];

/** What the page decides every frame and the scene then writes. */
export interface SceneVisibility {
  /** The OPTIONS `fluff` switch: the umbrella over every ambience piece. */
  fluff: boolean;
  /** The knob pack of `fast/knobs.ts`, read again on every `hashchange`. */
  knobs: Knobs;
  /** The zoom LOD gate (§10.17). False drops the four tactical layers. */
  detail: boolean;
  /**
   * The sonar switch, with whatever else the page gates it by already
   * applied. The scene ANDs the LOD gate onto it, as it does for the wake.
   */
  sonar: boolean;
  /**
   * The reader's chart-furniture pick (R548, §17). `off` hides both chart
   * layers and skips every tick. A page that draws no furniture — an arcade
   * match — leaves it unset, which is `off`.
   */
  chart?: ChartSetting;
  /**
   * The chart symbol every hold on the map wears (R633, §6.2): the
   * player's `holdSymbol` setting. The reading follows `chart`, and `off`
   * gives the modern one. A page that reads the pick one time — an arcade
   * match — leaves it unset, and the scene then keeps what it holds.
   */
  holdSymbol?: HoldSymbolId;
  /**
   * A replay film has the glass (R531). The furniture keeps its terrain
   * pieces under a film and gates every other one out.
   */
  filming?: boolean;
  /**
   * The groups of chart pieces the reader hid in the VIEW panel (R1812):
   * the setting `chartHide`. A page that hides none leaves it unset.
   */
  chartHide?: readonly string[];
}

/**
 * The visibility of a page that shows a still scene of the whole map: the
 * fluff on, the tactical layers on, no sonar and no chart. The theme
 * preview, the reel and the battle lab start from it, and each page owns
 * its record and changes a field of it.
 */
export function stillVisibility(): SceneVisibility {
  return {
    fluff: true, knobs: DEFAULT_KNOBS, detail: true, sonar: false, chart: 'off', filming: false,
  };
}

/** A camera that does nothing, for a page that writes the view itself: the
 *  theme preview and the reel. A hello does not move it either. */
export const STILL_CAMERA = {
  fitWorld(): void { /* the page places the view every frame */ },
  centerOn(): void { /* nor does a hello move it */ },
} as unknown as Camera;

/** What differs between the two pages that build a scene. */
export interface MapSceneOpts {
  /** The cache of server truth. The scene reads it and writes none of it. */
  state: GameState;
  /** The camera the hello centres and a grid change refits. */
  camera: Camera;
  /** A positional sound cue: a blast, a launch, a detonation. `voice` off
   *  plays the effect of the id and never its voice line (R595). */
  cue(id: string, x: number, y: number, scale?: number, voice?: boolean): void;
  /** The round style the player picked. The layer's own default without it. */
  torpedoStyle?: TorpedoStyle;
  /**
   * The terrain look the page opens in (R828). A page that knows its look
   * before it builds the scene passes it, and the layer then compiles that
   * pair and no other; a page that does not leaves `setLook` to arm it,
   * which costs one compile on the first frame after the theme lands.
   */
  terrainStyle?: TerrainStyle;
  /** A match shows the hull bar of an own contact at full health (R425). */
  alwaysShowOwnHullBar?: boolean;
  /** Where the ear is. A store and nothing more; the director reads it. */
  listener?(view: ViewRect, zoom: number, x: number, screenW: number): void;
  /** A full state frame replaced the whole owners map. */
  onOwnersReplaced?(): void;
  /** One cell changed hands. It runs between the flip and its ring. */
  onFlip?(index: number, to: number): void;
  /** The fleet layer took a state frame. */
  onFleetsApplied?(): void;
  /**
   * Whether the trench kraken may rise at all (R546). The arcade passes
   * `ARCADE_KRAKEN`, which the owner set false; the season map leaves it
   * unset, which is true. It sits over the `kraken` knob, so the knob can
   * take the beast off a page but never put it on one that refuses it.
   */
  kraken?: boolean;
  /**
   * Whether the chart furniture is built at all (R548). The arcade passes
   * `ARCADE_CHART`, which the owner set false; the season map leaves it
   * unset, which is true. False fills neither chart slot and builds no
   * controller, so a match carries none of it.
   */
  chart?: boolean;
  /**
   * The pieces a page draws although `chart` is false (R1580). The arcade
   * passes the sonar sweep, so a Vedette's scan looks as it does on the
   * season map. The controller then holds these pieces and no other.
   */
  chartPieces?: readonly AnyPiece[];
  /**
   * The page around the furniture: the `#furniture` host, the pointer and
   * the armed aim point. An arcade match and a test hand over nothing.
   */
  chartHost?: ChartHost;
  /**
   * Whether the readouts draw (R1535): the hull bars, the group badges,
   * the damage figures and the text of a hold's cartouche. The reel page
   * passes false, because a mode reel shows the fight and no figure. Every
   * other page leaves it unset, which is true.
   */
  labels?: boolean;
}

/**
 * The tubes of one class loaded with one kind, off the hello catalog, for
 * the tube reload look (R1474). The magazine is the one the fleet card's
 * TUBES bar divides by (R836). A fleet that sailed with no round still
 * carries tubes, so it takes the class's first kind: its empty tubes then
 * show the magazine they would fill.
 */
function reloadRound(state: GameState, cls: number, kind: number): ReloadRound {
  const spec = state.ship(cls);
  const k = kind !== 0 ? kind : (spec?.torpedoes[0] ?? 0);
  const round = state.torpedo(k);
  return { mag: magazineFor(spec, round), kind: k, name: (round?.name ?? '').toUpperCase() };
}

/**
 * The map both pages draw.
 *
 * Everything the layers hold is theirs: the owner bytes live in the
 * territory layer's data texture and the blocked mask in the terrain
 * layer's, so this object holds only what has no layer of its own.
 */
export class MapScene {
  readonly terrain: TerrainLayer;
  readonly territory: TerritoryLayer;
  /** The border lines of the territory, over the depth tints (R3241). */
  readonly borders: TerritoryBorders;
  readonly control: ControlLayer;
  readonly sonar: SonarLayer;
  readonly wake: WakeLayer;
  readonly fleets: FleetLayer;
  readonly torpedoes: TorpedoLayer;
  readonly fx: FxLayer;
  /**
   * The guard layer (R2080, §6.3.6): the round kill of every guard, which
   * every client that sees the kill draws, and the cooldown marks of the
   * guards whose clock this client holds (`state.guards`). It rides right
   * over `fx`, and under the gun shots.
   */
  readonly guard: GuardLayer;
  /**
   * The signature order looks and the deep run shroud (R2408, R2409,
   * §6.1.12). It rides right over `gatherover`, so a look reads over the
   * hulls and under the rounds and the hits. The fleet layer reads three
   * of its shares: the iris, the close of the rear dead zone and the fade
   * of CRASH DIVE.
   */
  readonly signature: SignatureLayer;
  /**
   * The player effects (R910, R914, §6.6). One class, two instances.
   *
   * `holdEffects` fills the `holdeffects` slot, right after `sites`, so a
   * hold's mark draws under every wake, hull and round. `effects` fills the
   * `effects` slot, right after `torpedoes`, so a hull's and a round's mark
   * draws over its own silhouette and under the blasts.
   */
  readonly effects: EffectLayer;
  readonly holdEffects: EffectLayer;
  /**
   * The gun shots and the hits (R1007, §6.3.3). One salvo per HIT event: the
   * rounds fly from the shooter to the victim and each one spawns the picked
   * hit mark when it arrives. It rides right over `fx`, and under the figures.
   */
  readonly combat: CombatLayer;
  /**
   * The Dredger harvesting looks (R1120, R1121, §6.2.1). ONE class, THREE
   * slots: `gatherunder` before `sites`, `gathermid` after `holdeffects` and
   * `gatherover` after `fleets`, because a look draws through three hooks and
   * the engine flushes one layer whole before the next.
   */
  readonly gather: GatherLayers;
  /** The damage figures (R538). They ride straight over the effects. */
  readonly damage: DamageLayer;
  readonly markers: MarkerLayer;
  readonly fog: FogLayer;
  readonly snow: SnowLayer;
  readonly haze: HazeLayer;
  readonly creatures: CreatureLayer;
  readonly kraken: KrakenLayer;
  /**
   * The monster bodies of round 109 (R2719, §6.1.15), in the slot
   * `monsters` right over `fleets`: the fleet layer takes the ragged ring
   * of each monster it draws away, and keeps the bars and the order line.
   */
  readonly monsters: MonsterLayer;
  /** The hatch of unsurveyed water (R2626). */
  readonly survey: SurveyLayer;
  /** The depth tints of card 09 (R2713, §17.3.09). */
  readonly depth: DepthLayer;
  /** The speed field of the current looks (R2827, §17.3.07). */
  readonly speed: SpeedFieldLayer;
  /** The sea objects of v2 phase 6 (R2657). */
  readonly seaObjects: SeaObjectLayer;
  /** The pirate dens of the reader, under `pirates` (R2720). */
  readonly dens: DenLayer;
  /** The lay and the collect looks of an own Sapper (R2657). */
  readonly sapperWork: SapperWorkLayer;
  /** The lanes of the faction for the Fanal, and the empire frame
   *  they were read from. */
  private laneLegsV: SeaLaneLeg[] = [];
  private laneFrame: unknown = null;
  /** The lanes of the faction as tracks for the guide of the chain
   *  (R3066), the empire frame they were read from, and the figures of the
   *  chain with the rules they were read from. */
  private laneTracksV: ChainTrack[] = [];
  private trackFrame: unknown = null;
  private chainFig: ChainFigures | null = null;
  private chainRules: unknown = null;
  /**
   * The chart furniture (R548, §17), or null on a page that draws none.
   *
   * It is not one layer but a controller over two: `chartsea` under the
   * beast and `chartscreen` over the fleets. The scene builds it because
   * both slots are the map's, and the page marks it stale through
   * `invalidate` on everything the map itself does not see.
   */
  readonly chart: ChartFurniture | null;
  /** False when the controller holds only the `chartPieces` (R1580). */
  private readonly chartWhole: boolean;

  /**
   * The site layer of the running world.
   *
   * A hello builds a NEW one: the layer is a dense record set keyed by the
   * sites that hello named, and a season roll or a resync replaces the whole
   * set. Everything that picks, describes or draws a site reads it here.
   */
  sites: SiteLayer | null = null;

  /** The player's own base, in world px. The hello sets it. */
  home = { x: 0, y: 0 };

  /** One entry per working sovereignty source (R116). A new array only
   *  when a frame changed the beam picture (R2442). */
  beams: UIBeam[] = [];
  /** True when the last state frame changed the beam picture (R2442). */
  beamsChanged = false;
  /** The first finds of the last state frame (R2626): the points of
   *  interest a delta added to the survey of the viewer's faction. A full
   *  frame restates the points and names none. */
  finds: readonly PointOfInterest[] = [];
  /** The beam rows this client holds, keyed by (beacon, source id). */
  private readonly beamPicture = new BeamPicture();
  /** The challenger and the gauge of every contested cell. */
  readonly contestByCell = new Map<number, { challenger: number; gauge: number }>();
  /** The sovereignty gauge of every cell, ×10 (§8). */
  readonly gaugeByCell = new Map<number, number>();
  /** Cells carrying a base: uncapturable (R66). */
  readonly baseCells = new Set<number>();
  /** Cells carrying a tier-2 strategic object. */
  readonly structureCells = new Set<number>();
  /** The viewer's squad, as account ids (R199). */
  squadIds = new Set<number>();
  /**
   * The canvas half of the look (themes.md §12, R814).
   *
   * The scene holds it for the same reason it holds the hold symbol: a
   * hello builds a NEW site layer, and the new one must open in the look
   * the player picked. The page writes it one time, after the scene is
   * built, because a theme cannot change during a match (§2).
   */
  private sceneLook: SceneLook = AUTO_SCENE_LOOK;
  /** The CURRENTS heat map was on at the last `setVisibility` (R2870). */
  private currentsHeatOn = false;
  /** The depth tints draw under the streaks: the gate of card 09, and no
   *  heat map of the client on (R2870, R2871). */
  private tintsUnder = false;

  /**
   * The hold symbol and its reading, as the last write left them (R633).
   * A hello builds a new site layer, and the pair is what it opens in.
   *
   * The symbol is READ by the chart's hold site (card 64, R964), which draws
   * the same broken hold the sites layer draws, so `ChartScene` names it and
   * this scene answers it.
   */
  holdSymbol: HoldSymbolId = DEFAULT_HOLD_SYMBOL;
  private holdEra: HoldEra = 'm';

  /**
   * The map marker look (R1291). The chart's map markers (card 66) READ
   * it, so `ChartScene` names it and this scene answers it out of the look.
   */
  get mapMarker(): MapMarkerStyle { return this.sceneLook.mapMarker; }

  /**
   * The faction key look (R1568). The chart's faction key (card 67) READS
   * it, so `ChartScene` names it and this scene answers it out of the look.
   */
  get factionKey(): FactionKeyStyle { return this.sceneLook.factionKey; }

  /**
   * The faction edge look (R1568, R3277). The faction key draws its border
   * samples in it, so `ChartScene` names it and this scene answers it out
   * of the look.
   */
  get factionEdge(): FactionEdgeStyle { return this.sceneLook.factionEdge; }

  /**
   * The active current look (R2716, R2869): the ONE answer to "which look
   * of the current field draws now". It is the World field `currentsHeat`
   * while the CURRENTS heat map is on (R2870), else `currents`. The chart's
   * card 07 READS it, so `ChartScene` names it, and the speed field takes
   * its figures from it. The workshop focus `currentsHeat` turns the heat
   * map on in its own state.
   */
  get currentLook(): CurrentLook {
    return this.opts.state.heatmap === HeatmapKind.HEATMAP_KIND_CURRENTS
      ? this.sceneLook.currentsHeat : this.sceneLook.currents;
  }

  /**
   * The look the depth tints draw in under the streaks (R2871), or null
   * while no tint draws there. Card 07 READS it, so `ChartScene` names it.
   */
  get tintLook(): DepthLook | null { return this.tintsUnder ? this.depthLook : null; }

  /**
   * The depth look the map draws (R2713, R2726): the World field `depth`,
   * flipped for the session by the key `\`. The legend card READS it, so
   * `ChartScene` names it and this scene answers it.
   */
  get depthLook(): DepthLook { return depthLookOf(this.sceneLook.depth); }
  /** The terrain look the layer draws (R828). Card 13 reads it (R3276). */
  get terrainLook(): TerrainStyle { return this.terrain.style; }

  /**
   * The point of interest set (R2714). The chart's points of interest
   * (card 69) READ it, so `ChartScene` names it and this scene answers it
   * out of the look.
   */
  get poi(): PoiSet { return this.sceneLook.poi; }

  /** The reading the holds wear (R633), which the faction key reads (R1568)
   *  and the marks key of the squad chip draws its holds in (R1571). */
  get markEra(): HoldEra { return this.holdEra; }

  /** The knob pack the fluff was last built at. */
  private knobs: Knobs = readKnobs();
  private knobsSeen = false;

  /**
   * The hulls whose MARK another layer draws: a live hit mark of the combat
   * layer (R1012) or a monster body (R2719). One object, read on each call.
   */
  private readonly hullMarks = {
    has: (id: number): boolean => this.combat.drawnHulls.has(id) || this.monsters.drawn.has(id),
  };

  /** What the last `setVisibility` decided, which the feed then obeys. */
  private detail = true;
  private sonarOn = false;
  private wakeOn = false;

  /** The sonar rings, converted from mpx once per empire frame (R131). */
  private ringsWire: unknown = null;
  /** The commission list the marker layer last packed (R2645): the layer
   *  packs again only when the empire frame hands over a new one. */
  private commWire: unknown = null;
  /**
   * The listening circles this commander receives, in world px.
   *
   * The chart's light characteristics (card 17) letter every one of them, so
   * the converted array is read and not private. It is rebuilt only when the
   * empire frame hands over a new one, and a reader keeps nothing out of it.
   */
  ringsPx: { x: number; y: number; range: number }[] = [];
  /** The fleet snapshot of this frame (`FleetLayer.info`), for the listener
   *  of the hostile view of a lay (R2841). A reader keeps nothing out of it. */
  private frameFleets: readonly FleetInfo[] = [];

  /** The grid callback this scene installed, so `dispose` drops only its own. */
  private readonly gridHook: () => void;
  private disposed = false;
  /** Stops the marker repack, which listens on the atlas. */
  private readonly dropMarkerRepack: () => void;
  /** Which gun hit of a fight speaks (R629). */
  private readonly fight = new FightClock();
  /** The gap of the cell flip cue (R3120). */
  private readonly flipGap = new CueGap();

  constructor(
    private readonly engine: Engine,
    private readonly opts: MapSceneOpts,
  ) {
    this.terrain = new TerrainLayer(engine, opts.terrainStyle);
    this.territory = new TerritoryLayer(engine);
    this.borders = new TerritoryBorders(this.territory);
    this.control = new ControlLayer(engine);
    this.sonar = new SonarLayer(engine);
    this.wake = new WakeLayer(engine);
    this.fleets = new FleetLayer(engine);
    this.fleets.labels = opts.labels !== false;
    this.torpedoes = new TorpedoLayer(engine, opts.torpedoStyle);
    // The two effect instances (R914). The hold feed reads `this.sites` on
    // every call, so it follows the new site layer a hello builds.
    this.effects = new EffectLayer('effects', engine, {
      fleets: () => this.fleets.chartFleets(),
      rounds: () => this.torpedoes.rounds(),
    });
    this.holdEffects = new EffectLayer('holdeffects', engine, {
      holds: () => this.sites?.holds() ?? NO_HOLDS,
    });
    this.fx = new FxLayer(engine);
    // The guard layer reads the clocks of the state and places each guard
    // on its host: a hull on the fleet layer, a structure on its cell.
    this.guard = new GuardLayer(engine, {
      rounds: () => this.torpedoes.rounds(),
      clocks: () => this.opts.state.guards,
      at: (g) => (g.kind === 'fleet' ? this.fleets.at(g.id) : this.siteAt(g.id)),
      pip: (g, out) => {
        if (g.kind === 'fleet') {
          // A hull's pip is the clock face of the tube reload, and it
          // scales about the hull with the hull's marks (R2418).
          const F = RELOAD_LOOKS['clock-digit'].fig, k = this.fleets.markScale;
          out.dx = F.x_px * k; out.dy = F.y_px * k; out.r = F.r_px * k;
          return;
        }
        const off = GUARD.structureSizePx + GUARD.pipGapPx;
        out.dx = off; out.dy = -off; out.r = GUARD.pipPx;
      },
      worldSec: (s) => this.opts.state.mapToWorldSec(s),
    });
    // The signature order looks (R2408). Every hull it draws is the fleet
    // layer's, and every figure of a rule comes off the hello catalog.
    this.signature = new SignatureLayer(engine, {
      pose: (id, out) => this.fleets.signaturePose(id, out),
      foe: (x, y, faction, r) => this.fleets.nearestFoe(x, y, faction, r),
      orderOf: (cls) => this.opts.state.ship(cls)?.signature ?? SignatureOrder.SIGNATURE_ORDER_NONE,
      noiseMul: () => this.opts.state.rules.approach?.crashDiveNoiseMul ?? 0,
      mapSecPerWallSec: () => this.opts.state.tacticalScale,
    });
    this.fleets.signature = this.signature;
    // The monster bodies (R2719). The place and the bow are the fleet
    // layer's, so a body stands where the hull would stand.
    this.monsters = new MonsterLayer(engine, {
      pose: (id, out) => this.fleets.signaturePose(id, out),
      foe: (x, y, r) => this.fleets.nearestFoe(x, y, 0, r),
      foes: (x, y, r, out) => this.fleets.foesWithin(x, y, 0, r, out),
      mapSecPerWallSec: () => this.opts.state.tacticalScale,
    });
    // A quill pod wears the design of its Quillback (R2719).
    this.torpedoes.podDesignAt = (x, y) => this.monsters.podDesignAt(x, y);
    // The whole wait of a Stonefish in wall seconds (R2848): the catalog
    // `loiter_sec` over the map clock, so the fuse runs on the client's
    // clock between two steps of `loiter_pct`.
    this.torpedoes.waitSecOf = (kind) => {
      const spec = this.opts.state.torpedo(kind), k = this.opts.state.tacticalScale;
      return spec && spec.loiterSec > 0 && k > 0 ? spec.loiterSec / k : 0;
    };
    // The gun shots and the hits (R1007). Every answer it needs about a hull
    // comes off the fleet layer, so the wire learns nothing new and the class
    // of an undetected shooter cannot leak (R1009).
    this.combat = new CombatLayer(engine, {
      at: (id) => this.fleets.at(id),
      shieldOf: (id) => this.fleets.shieldOf(id),
      pose: (id) => this.fleets.combatPose(id),
      near: (x, y, r) => this.fleets.nearestClass(x, y, r),
      ownerOf: (id) => this.fleets.ownerOf(id),
      // The calm gate of the shield regen in wall seconds (R1979).
      fightGapSec: () => {
        const s = this.opts.state;
        return s.tacticalScale > 0 ? s.regenCalmMapSec / s.tacticalScale : 0;
      },
    });
    // The Dredger harvesting looks (R1120). The hull feed is the fleet
    // layer's own walk of every hull the viewer may read a mission for, and
    // the field feed reads `this.sites` on every call, so it follows the new
    // site layer a hello builds.
    this.gather = new GatherLayers(engine, {
      hulls: (fn) => this.fleets.gatherers(fn),
      field: (x, y, r) => this.sites?.fieldNear(x, y, r) ?? null,
    });
    // The two pieces every player sees read the same tracker the harvesting
    // looks do, so a cut cannot read one way on the hull and another on the
    // water (R1120).
    this.fleets.setGatherState(this.gather.state);
    // The figure follows its hull, so the layer asks the fleet layer where
    // that hull is on every frame rather than freezing the hit's point.
    this.damage = new DamageLayer(engine, (id) => this.fleets.at(id));
    // The Tender's pluses (R725). The fleet layer knows when the work lands
    // — the wire raised a tended hull's percent, or a regen ring passed a
    // hull in an aura — and the damage layer owns every figure that stands
    // beside a bar, so the one hands the other the mark.
    this.fleets.onHeal = (id, x, y, kind, faction) => {
      this.damage.heal(id, x, y, kind, faction);
    };
    // The two atlas hooks a static pack listens on: a grow moves every cell,
    // and the glyph refresh re-lays every run of text. The recording engine
    // of the tests carries both hooks too, and neither ever fires there.
    const atlas = engine.atlas;
    // A drill step's hull mark follows its hull, so the layer asks the
    // fleet layer where it stands on every frame (arcade.md §17.8).
    this.markers = new MarkerLayer(engine, (id) => this.fleets.combatPose(id));
    // The marks are packed once per hello, so an atlas grow must pack them
    // again with the moved cells.
    this.dropMarkerRepack = atlas.onRepacked(() => this.markers.repack());
    // The fluff (R101, R499). Each layer scatters itself in its constructor
    // at the default counts; `applyKnobs` re-scatters for a page that
    // arrived with a count knob set, and the grid hook below re-scatters for
    // a world whose rectangle is not this client's default.
    this.fog = new FogLayer(engine);
    this.snow = new SnowLayer(engine);
    this.haze = new HazeLayer(engine);
    this.creatures = new CreatureLayer(engine);
    // The kraken is the one piece of fluff ABOVE the terrain (R522, R525).
    this.kraken = new KrakenLayer(engine);
    // With `pirates` on, the beast rises in no trench cell the survey of
    // the viewer holds, because a monster of v2 §8.3 hunts there (R2606,
    // R2626). The scene reads the flag; the layer reads the answer.
    this.kraken.avoid = (cell) => {
      const st = this.opts.state;
      return st.hasFeatureFlag('pirates') && st.survey.on && st.survey.has(cell);
    };
    this.survey = new SurveyLayer(engine);
    // On paper the bands are the ground of the sea, so the pass draws under
    // the terrain and the creatures (R3309).
    this.depth = new DepthLayer(engine, LIGHT ? 'depthfloor' : 'depth');
    // The speed field (R2827): it draws while the chart shows the piece,
    // asked at the draw, because a film takes the scene's frame away.
    this.speed = new SpeedFieldLayer(engine, () => this.chart?.shows(currentFlow) === true);
    // The sea objects (R2657): the rows of the store, which reads no flag.
    this.seaObjects = new SeaObjectLayer(engine, {
      rows: () => this.opts.state.seaObjects.list,
      rev: () => this.opts.state.seaObjects.rev,
      viewer: () => this.opts.state.faction,
      sonarPx: (k) => this.opts.state.seaObject(k)?.sonarPx ?? 0,
      laneLegs: () => this.factionLaneLegs(),
      hidden: () => this.sapperWork.drawn,
      anchorFrac: (r) => this.opts.state.seaObjects.anchorFrac(r, Date.now()),
      // The guide of the chain (R3066): the wire sends the lanes under
      // `routes` and the figures under `sapper`, so it reads no flag.
      chain: {
        tracks: () => this.factionLaneTracks(),
        sappers: () => this.opts.state.seaObjects.ownSappers,
        selected: () => this.fleets.selectedId,
        figures: () => this.chainFigures(),
        pose: (id) => this.fleets.combatPose(id),
      },
      // The relay points of the lit road (R3072): the stretches of card 16
      // while the chart shows it, so a hidden card draws no point.
      relays: () => (this.chart?.shows(shippingLanes) ? shippingLanes.relays : NO_RELAYS),
    });
    // The pirate dens (R2720): the rows of the Empire frame, none while
    // `pirates` is off.
    this.dens = new DenLayer(engine, {
      dens: () => denRows(this.opts.state),
      fragments: () => fragmentRows(this.opts.state),
      leftSec: (d) => denLeftSec(this.opts.state, d),
    });
    // The work of an own Sapper (R2657): the store reads the lays and the
    // collects off the rows, and the fleet layer places the hull.
    this.sapperWork = new SapperWorkLayer(engine, {
      pose: (id) => {
        const p = this.fleets.combatPose(id);
        return p ? { x: p.x, y: p.y, head: p.head } : null;
      },
      row: (id) => this.opts.state.seaObjects.row(id),
      loudLeftSec: (id, now) => this.opts.state.seaObjects.loudLeftSec(id, now),
      // The hostile view of a lay (R2841): the loud lay the store saw, and
      // the viewer's nearest ear for the bearing.
      foeLoudLeftSec: (id, now) => this.opts.state.seaObjects.foeLoudLeftSec(id, now),
      listener: (x, y) => nearestListener(this.ringsPx, this.frameFleets, x, y),
      cargoCap: () => this.opts.state.ship(ShipClass.SHIP_CLASS_SAPPER)?.cargo ?? 0,
      mapPerWall: () => this.opts.state.tacticalScale,
      sonarPx: (k) => this.opts.state.seaObject(k)?.sonarPx ?? 0,
      // The cache work look of SEARCH (R2833).
      works: () => this.fleets.ownWorks(),
    });
    this.fleets.alwaysShowOwnHullBar = opts.alwaysShowOwnHullBar ?? false;

    // The site slot is NOT filled here: a hello replaces the whole site set,
    // so `applyHello` builds the layer and fills the slot itself.
    // Every layer carries the name of the slot it fills.
    // The list is the order of SCENE_SLOTS, less `sites`. The three gather
    // passes carry the names of their own slots and stand where they draw:
    // under the field, over the field, and over the hull (R1120).
    // The fleet layer fills the `deadzone` slot as well, with the dead zone
    // disc it packs (R1472).
    for (const l of [
      this.terrain, this.territory, this.fleets.deadZone, this.control, this.sonar,
      this.gather.passes[0], this.dens, this.seaObjects, this.holdEffects, this.gather.passes[1],
      this.wake, this.fleets, this.monsters, this.gather.passes[2], this.sapperWork, this.signature,
      this.torpedoes, this.effects, this.fx, this.guard, this.combat,
      this.damage, this.markers, this.fog, this.snow, this.haze, this.creatures,
      this.kraken, this.survey, this.depth, this.speed, this.borders,
    ]) engine.setLayer(l.name, l);

    // The chart furniture (R548). It is built last, because it reads the
    // scene the other layers hold, and it fills both of its slots at once.
    // A page that draws no furniture builds nothing and leaves the two
    // slots the `NullLayer`s the engine put there.
    const few = opts.chart === false ? opts.chartPieces ?? [] : null;
    this.chartWhole = few === null;
    if (few === null || few.length > 0) {
      this.chart = new ChartFurniture({
        src: engine,
        state: opts.state,
        scene: this,
        host: opts.chartHost ?? null,
        pieces: few ?? undefined,
        onGlyphs: (fn) => atlas.onGlyphsRefreshed(fn),
        onRepacked: (fn) => atlas.onRepacked(fn),
      });
      engine.setLayer('chartsea', this.chart.sea);
      engine.setLayer('chartscreen', this.chart.screen);
    } else this.chart = null;

    // A world whose grid is not this client's default refits here (R261),
    // and the layers that scatter over the world rectangle scatter again.
    this.gridHook = () => {
      opts.camera.fitWorld();
      this.fog.scatter(this.knobs);
      this.snow.scatter(this.knobs);
      this.haze.rebuild(this.knobs.hazeb);
    };
    engine.onGrid = this.gridHook;
  }

  /**
   * The look of everything the map draws (R814, R823): the terrain, the
   * marks on the hulls and the holds, the bars, the badges and the two
   * Tender marks. One object, handed to the three layers that own those
   * marks, because a layer that read one field would let two of them
   * disagree.
   */
  setLook(l: SceneLook): void {
    // The map markers draw in the look (R1291), so a new look packs them
    // again. The chart reads the pick through `mapMarker` below.
    if (l.mapMarker !== this.sceneLook.mapMarker
      || l.factionKey !== this.sceneLook.factionKey
      || l.factionEdge !== this.sceneLook.factionEdge
      || l.currents !== this.sceneLook.currents
      || l.currentsHeat !== this.sceneLook.currentsHeat
      || l.poi !== this.sceneLook.poi
      // Card 13 rings each ridge under the terrain look `chart` (R3276).
      || l.terrain !== this.sceneLook.terrain) this.chart?.invalidate('look');
    this.sceneLook = l;
    // The look of the territory edge (R1568 card 04 row a of round 075,
    // R3277 card 06 of round 145).
    this.territory.setEdge(l.factionEdge);
    this.fleets.setLook(l);
    this.sites?.setLook(l);
    this.terrain.setStyle(l.terrain);
    this.combat.setLook(l);
    this.gather.setLook(l);
    this.seaObjects.setLook(l);
    this.sapperWork.setLook(l);
    // The detonation look (R2079).
    this.fx.setDetonationLook(l.detonation);
  }

  /**
   * The look of every hold and every installation (R633, §6.2).
   *
   * The scene holds the pair, because a hello builds a new site layer and
   * the new one must open in the look the player picked. The season map
   * writes it every frame through `setVisibility`; an arcade match writes
   * it one time, at the start, as it does the damage style.
   */
  setHoldStyle(symbol: HoldSymbolId, era: HoldEra): void {
    this.holdSymbol = symbol;
    // The faction key draws its badges in this reading (R1568).
    if (era !== this.holdEra) this.chart?.invalidate('look');
    this.holdEra = era;
    this.sites?.setStyle(symbol, era);
    // The charge pip of a hull is drawn in the reading the holds wear
    // (R1568).
    this.fleets.setPipEra(era);
  }

  /**
   * The round style (R806). The constructor takes the style the page read
   * at boot; this changes it later, and the theme preview and a Renderer
   * theme both write it. The layer drops the shed marks of the rounds in
   * flight on a change and keeps the rounds.
   */
  setTorpedoStyle(style: TorpedoStyle): void {
    this.torpedoes.setStyle(style);
  }

  /** The owner byte of every cell. The territory layer's texture array. */
  get owners(): Uint8Array { return this.territory.owners; }

  /** The blocked mask of the running world, or null before the hello. */
  get blocked(): Uint8Array | null { return this.terrain.blocked; }

  /**
   * The terrain plane the chart traces its isobaths off (R548, §17.3.13),
   * or null while the prepare has not landed.
   */
  get plane(): TerrainPlane | null { return this.terrain.plane; }

  /**
   * The water-lining of card 01 (R548, §17.3.01).
   *
   * The chart piece owns no batch: it writes the terms of the terrain
   * program through this one call.
   */
  setWaterLines(on: boolean, cfg?: ChartLineCfg): void {
    this.terrain.setWaterLines(on, cfg);
  }

  /**
   * The depth tints of card 09 (R2713, §17.3.09): the chart piece hands
   * the band field and the reading to the depth layer through this call.
   */
  setDepthField(reading: ChartReading, bands: Uint8Array | null): void {
    // On paper the bands are the ground under the terrain (R3309), so a rock
    // cell takes the band of the water that touches it, and no cream corner
    // of the hole stands round a smooth ridge (R3310).
    this.depth.setField(reading, LIGHT && bands
      ? fillRockBands(bands, this.terrain.blocked, COLS, ROWS, BLOCK_RIDGE) : bands);
  }

  /**
   * The speed field of the current looks (R2827, §17.3.07): the chart
   * piece hands the flow grid and the strongest current to the speed
   * field layer through this call.
   */
  setSpeedField(grid: FlowGrid | null, strongest: number): void {
    this.speed.setField(grid, strongest);
  }

  /** The nodes of the speed field a survey pass moved (R2897). */
  patchSpeedField(grid: FlowGrid, strongest: number, patch: FlowGridPatch): void {
    this.speed.patchField(grid, strongest, patch);
  }

  /**
   * The camera shake this scene asks for, in CSS px.
   *
   * It is the torpedo jolt and nothing else. The jolt is zeroed with the
   * layer: a round layer that does not advance holds its jolt clock still,
   * and a live offset off a frozen layer would shake the camera for ever.
   */
  get shakeX(): number { return this.detail ? this.torpedoes.shakeX : 0; }
  get shakeY(): number { return this.detail ? this.torpedoes.shakeY : 0; }

  // -- the knobs -----------------------------------------------------------

  /**
   * The knob pack, at boot and again on every `hashchange` (R431, R493).
   *
   * A count knob re-seeds the two ambience layers together, because fog and
   * snow replay one shared random sequence (`ambience.ts`); the band knob
   * rebuilds the haze; the rest is a cheap `configure` the layers compare
   * against what they hold. The visibility knobs are read in the frame.
   */
  applyKnobs(k: Knobs): void {
    const first = !this.knobsSeen;
    this.knobsSeen = true;
    const rescatter = first || k.fogn !== this.knobs.fogn || k.snown !== this.knobs.snown;
    const rehaze = first || k.hazeb !== this.knobs.hazeb;
    this.knobs = k;
    if (rescatter) { this.fog.scatter(k); this.snow.scatter(k); }
    if (rehaze) this.haze.rebuild(k.hazeb);
    this.creatures.configure(k.whales === 1, k.whalen);
    this.kraken.configure(this.opts.kraken !== false && k.kraken === 1, k.beast);
    this.wake.setCap(k.waken);
    this.chart?.setOnly(chartOnly(k.chart));
  }

  // -- the frame -----------------------------------------------------------

  /**
   * What each layer the scene owns shows this frame.
   *
   * A switch that is off hides the layer AND skips its per-frame work
   * (§10.3) — an invisible layer is neither advanced nor drawn. The wake and
   * the sonar ride the LOD gate as well: the wake's input is the fleet
   * snapshot, and a wake that keeps drawing off a frozen snapshot smears one
   * still picture across the sea.
   */
  setVisibility(v: SceneVisibility): void {
    const k = v.knobs;
    this.detail = v.detail;
    this.wakeOn = v.fluff && k.wake === 1 && v.detail;
    this.sonarOn = v.sonar && v.detail;
    this.fleets.setVisible(v.detail);
    this.torpedoes.setVisible(v.detail);
    this.guard.setVisible(v.detail);
    this.signature.setVisible(v.detail);
    this.monsters.setVisible(v.detail);
    this.seaObjects.setVisible(v.detail);
    this.dens.setVisible(v.detail);
    this.sapperWork.setVisible(v.detail);
    this.sapperWork.setFilming(v.filming === true);
    // A viewer cannot switch another player's mark off (the owner's brief):
    // the knob is a measurement and eye-test switch, and the LOD gate is the
    // one the hulls and the rounds already ride (R914).
    const effectsOn = v.detail && k.effects === 1;
    this.effects.setVisible(effectsOn);
    this.holdEffects.setVisible(effectsOn);
    this.effects.setPreview(k.fxfleet, k.fxround, 0);
    this.holdEffects.setPreview(0, 0, k.fxhold);
    // The gun shots and the hits (R1007). `#shots=0` takes the layer off and
    // `applyEvents` then draws the ring and the boom the client drew before it.
    // It is a measurement and eye-test knob, exactly as `effects` is.
    const shotsOn = k.shots === 1;
    this.combat.setEnabled(shotsOn);
    this.combat.setVisible(v.detail && shotsOn);
    this.combat.setForced(combatForcedOf(k));
    // A live final-blow mark draws the hull itself, so the fleet layer takes
    // its own instance away for that hull (R1007). A hull hit that draws the
    // silhouette takes the MARK alone: the fleet is alive, and it keeps its
    // bars, its badge and its group mark for the whole fight (R1012).
    this.fleets.setSuppressed(this.combat.ownedHulls);
    this.fleets.setHullSuppressed(this.hullMarks);
    // The Dredger harvesting looks (R1120). `#gather=0` takes the three
    // passes off, and with them the tracker, so the two always-on pieces of
    // the fleet layer stop with the looks (R1122). A look that gauges the
    // hold draws the laden ring itself, and a look that draws the field mark
    // takes the site layer's own sprite away.
    const gatherOn = k.gather === 1;
    this.gather.setEnabled(gatherOn);
    this.gather.setVisible(v.detail && gatherOn);
    this.gather.setForced(k.gatherlook);
    // The anchoring look (R1125): `#anchoring=<id>` puts one look on every
    // anchor hull, as `#gatherlook` does for a cut.
    this.fleets.setAnchorForced(k.anchoring);
    // The hold-fire look (R1439): `#holdfire=<id>` puts one look on every
    // own hull that holds fire.
    this.fleets.setHoldFireForced(k.holdfire);
    // The tube reload look (R1474): `#reload=0` takes it off, and
    // `#reloadlook=<id>` puts one look on every own hull with tubes.
    this.fleets.setReloadOn(k.reload === 1);
    this.fleets.setReloadForced(k.reloadlook);
    // The detonation look (R2079): `#detonationlook=<id>` puts one look on
    // every round that goes off.
    this.fx.setDetonationForced(k.detonationlook);
    this.fleets.setLadenSuppressed(this.gather.ladenSuppressed);
    this.sites?.setFieldSuppressed(this.gather.drawnFields);
    this.wake.setVisible(this.wakeOn);
    this.fog.setVisible(v.fluff && k.fog === 1);
    this.snow.setVisible(v.fluff && k.snow === 1);
    this.haze.setVisible(v.fluff && k.haze === 1);
    this.creatures.setVisible(v.fluff && k.whales === 1);
    this.kraken.setVisible(v.fluff && this.opts.kraken !== false && k.kraken === 1);
    this.sonar.setVisible(this.sonarOn);
    // The chart furniture answers its own pick and nothing else (R548). It
    // is NOT gated by the LOD, because a place name and an isobath are the
    // sea floor and not a tactical layer; each piece states its own zoom
    // band instead. The film flag reaches it even while `off`, so the frame
    // a film opens on needs no second write.
    const reading = v.chart ?? 'off';
    this.chart?.setFilming(v.filming === true);
    this.chart?.setReading(reading);
    this.survey.setReading(reading === 'historical');
    this.seaObjects.setReading(reading);
    this.sapperWork.setReading(reading);
    this.chart?.setHidden(v.chartHide ?? NO_HIDDEN);
    // The hold symbols (R633, §6.2). They are NOT furniture — a hold is an
    // actionable object and is drawn whatever `chart` says — but the
    // READING of the symbol is the chart's, and `off` gives the modern one.
    // A page that wrote the pick itself passes none here and keeps it.
    if (v.holdSymbol) {
      this.setHoldStyle(v.holdSymbol, reading === 'historical' ? 'h' : 'm');
    }
    // The limit line (card 34) draws the hovered site's listening circle
    // while the furniture is on, so the sonar layer's own hover ring stands
    // down then and comes back with the furniture off (R563).
    // A controller of a few pieces holds no limit line, so the ring stays.
    this.sonar.hoverRingDrawn = !this.chartWhole || reading === 'off';
    // Card 01 owns no batch (R548, §17.3.01): its picture is a term
    // inside the terrain program. The piece writes it
    // on while its gate holds, and the scene writes them off here, because
    // the controller never calls a piece the gate has dropped. `setReading`
    // above drops every piece from the gate the moment the pick is `off`,
    // so `shows` answers the reading on this frame and a form-factor change
    // one frame later. Both readings draw the lines (the owner's ruling of
    // 2026-09-05), so the gate is the piece's own and no test of the
    // reading stands beside it (R1329).
    if (!this.chart?.shows(waterLines)) this.setWaterLines(false);
    // Card 09 owns no batch either (R2713, §17.3.09): the depth layer draws
    // while the piece's gate holds, and keeps its field while it does not.
    // The shoal stipple (card 71) and the edge seam (card 72) are marks of
    // the same pass: the gate of each piece (the pack, the Chart theme, the
    // DEPTH group) turns its mark on, in the feathered look alone (R2726).
    // A new look builds the legend card again, because its band swatches
    // show it.
    //
    // The two heat maps of the client (R2870): CURRENTS draws the current
    // heat look and hides the tints; DEPTH draws the tints in the depth
    // heat look, and card 07 hides itself (`CurrentFlowPiece.visible`), so
    // the streaks and the speed field go with it. The furniture shows the
    // group of either map while the VIEW pill hides it (`setHidden`).
    const heat = this.opts.state.heatmap;
    const heatCurrents = heat === HeatmapKind.HEATMAP_KIND_CURRENTS;
    const heatDepth = heat === HeatmapKind.HEATMAP_KIND_DEPTH;
    if (heatCurrents !== this.currentsHeatOn) {
      this.currentsHeatOn = heatCurrents;
      this.chart?.invalidate('look');
    }
    const tints = this.chart?.shows(depthTints) === true && !heatCurrents;
    this.depth.setGate(tints);
    // The streaks follow the bands while the tints draw under them (R2871).
    this.tintsUnder = tints && !heatDepth;
    const heatLook = this.depth.setHeatLook(heatDepth);
    if (this.depth.setMarks(this.depthLook,
      this.chart?.shows(shoalStipple) === true, this.chart?.shows(edgeSeam) === true) || heatLook) {
      this.chart?.invalidate('look');
    }
    // The speed field of card 07 owns no batch either (R2827, §17.3.07):
    // the layer reads the piece's gate at the draw, and takes the look and
    // the reading here.
    this.speed.setLook(lookIndex(this.currentLook), this.chart?.drawnReading ?? 'modern');
  }

  /**
   * The per-frame feed of everything the scene owns.
   *
   * One `fleets.info()` snapshot serves three consumers, the ring array is
   * rebuilt only when the empire frame hands over a new one, and the
   * ping-lit snow reads THIS frame's wavefronts — which is why the sonar
   * runs before it. A frame allocates nothing else (R131).
   */
  frame(info: FrameInfo, vis: SceneVisibility): void {
    this.setVisibility(vis);
    const { engine, opts } = this;
    const state = opts.state;
    // Where the ear is. A store and nothing more — the director reads it
    // when a map cue fires, and does no work otherwise. The rect is
    // the shake-free one the frame already computed.
    opts.listener?.(
      info.view, engine.view.scale.x, engine.view.x, HAS_WINDOW ? window.innerWidth : 0,
    );

    // One snapshot, three consumers: the sonar, the ping-lit snow and the
    // wake. The buffer is reused between frames (R131) — read it now, keep
    // nothing.
    const fleets = this.fleets.info();
    this.frameFleets = fleets;
    // The listening circles the server says this commander receives (R112).
    // Nothing is derived here: which sources count is a fog-of-war rule.
    const wire = state.empire?.sonarRings ?? null;
    if (wire !== this.ringsWire) {
      this.ringsWire = wire;
      this.ringsPx = wire?.map((r) => ({
        x: r.xMpx / MPX_PER_PX, y: r.yMpx / MPX_PER_PX, range: r.rangePx,
      })) ?? [];
    }
    // The commission marks (v2 phase 5, R2645): the targets the wire lists,
    // packed once per new list. The layer reads no flag.
    const comm = state.empire?.commissions ?? null;
    if (comm !== this.commWire) {
      this.commWire = comm;
      this.markers.setCommissions(comm ?? [], state.faction);
      // The fleets that work a commission wear its ink on their order
      // (R2833): a taken offer that is not done yet.
      this.fleets.setCommissioned(new Set((comm ?? []).filter((c) => c.fleetId !== 0 && !c.done).map((c) => c.fleetId)));
    }
    // The ping cue rides `onPing` from inside the layer's own advance, so a
    // layer that does not advance makes no sound either.
    // The sweep ring of an own Sapper takes the A4 lift (R2635, R2657),
    // both figures off the hello catalog (R2659). A catalog built in part
    // (a test, an arcade stand-in) holds no tree.
    if (this.sonarOn) {
      const sr = state.rules.seaObjects;
      const lift = sr && state.catalog?.tech && state.hasTechCode('A4') ? sr.sweepRadiusMul : 1;
      this.sonar.sweepPx = sr ? sr.sweepPx * lift : 0;
    }
    // The collect ring of the selected own Sapper (R2711), off the hello
    // catalog, while the world latched `sapper`.
    this.fleets.collectPx = state.hasFeatureFlag('sapper') ? state.rules.seaObjects?.collectPx ?? 0 : 0;
    // The hull under the pointer draws its primary reach (R2738). The hit
    // test writes the hover on the sonar layer, so both rings read one id.
    this.fleets.hoverId = this.sonar.hoverFleetId;
    if (this.sonarOn) this.sonar.feed(state.faction, this.ringsPx, fleets, this.fleets.selectedId);
    // Sonar off means no wavefronts (R101: particles only, never contacts).
    // One array, two readers: the snow grains and the beast (R525).
    const fronts = this.sonarOn ? this.sonar.fronts() : NO_FRONTS;
    this.snow.feed(fronts, vis.knobs.lit === 1);
    // The abyss glimmer (ui-map-interface.md §7): the snow sorts by band
    // only while the depth tints draw; null draws the snow of today.
    this.snow.setBands(this.depth.visible ? state.depthBands : null);
    this.kraken.feed(fronts);
    // The wake's own-faction gate lives in the layer; this hands it the
    // faction to compare against.
    if (this.wakeOn) this.wake.feed(fleets, state.faction);
    // The edge seam of the depth tints stands near the own hulls (R2713),
    // and the lanterns light the own hulls and the own holds (R3229).
    if (this.depth.visible) this.depth.feedHulls(fleets, this.sites?.holds() ?? NO_HOLDS, state.playerId, info.view);
    // The speed field scales by the tide now: a uniform, no texel (R2827).
    if (this.speed.visible) this.speed.setTide(state.tideFactor());
    // The furniture's own tick: the context, the fade and every rebuild a
    // reason marked. A film takes this frame away from the scene, and the
    // two chart layers then call the same tick themselves (R531, R548).
    this.chart?.frame(info, vis.filming === true);
  }

  // -- the hello -----------------------------------------------------------

  /**
   * Builds the world the hello describes.
   *
   * The caller has stored the hello or not; this arm reads only the frame it
   * was handed, so the two pages can store it on either side of this call.
   */
  applyHello(h: Hello): void {
    const { engine, opts } = this;
    // The grid is this world's, not a client constant (R261). It must be
    // applied before anything reads the geometry: the engine's data textures
    // are one texel a cell, and the grid hook fits the rectangle.
    engine.setGrid(h.cols, h.rows);

    // The banner this viewer sails under, for the contact readout (R425). A
    // hull of another faction wears the pair of bars under it; a hull of
    // this one never does.
    this.fleets.viewerFaction = h.faction;
    // The bow heading of the approach (R2376): every world plays the
    // approach (R2400), so a page with a hello draws the bow.
    this.fleets.bowHeading = true;
    if (h.catalog?.anchorCycleSec) this.fleets.anchorCycleSec = h.catalog.anchorCycleSec;
    // The beam order of a SET Beacon (R2858), and the clock a look prints on
    // (R1125): the share comes off the catalog and the time off the state's
    // own conversion, so the client mirrors no rule.
    if (h.catalog?.beaconBeamSec) this.fleets.beaconBeamSec = h.catalog.beaconBeamSec;
    this.fleets.worldSecOf = (s) => this.opts.state.mapToWorldSec(s);
    // The tube reload look (R1474) runs the reload down at the map's pace
    // between two frames, and reads the magazine and the round the fleet
    // card reads (R836), off this catalog.
    this.fleets.mapSecPerWallSec = () => this.opts.state.tacticalScale;
    this.fleets.setRounds((cls, kind) => reloadRound(this.opts.state, cls, kind));
    // The detonation look (R2079) reaches out to the splash of the round's
    // kind and weighs its damage, off this catalog; a kind the catalog does
    // not carry takes the pack's row.
    this.fx.setRounds((kind) => this.opts.state.torpedo(kind) ?? packRound(kind));
    // The fixed order of the ship rounds (R2017): the firing kind of a
    // mixed load and the order of its pips read it (R2031).
    this.fleets.roundOrder = roundOrder(h.catalog?.torpedoes ?? []);
    // The reach of a set Tender's aura, from the applied pack (R723). The
    // regen wave grows to it, so the ring shows the server's figure and
    // never a figure the client keeps.
    this.fleets.tenderAuraPx = h.catalog?.rules?.tenderAuraPx ?? 0;
    // The heal beam reach of a Tender, from the applied pack (R2738): the
    // ring of a selected or hovered Tender shows the server's figure.
    this.fleets.beamPx = h.catalog?.rules?.tenderRepairRangePx ?? 0;
    // The reach of a held Coral Ward's aura, from the applied pack
    // (R1082). Both layers read it: the fleet layer raises the shield plus
    // inside it, and the site layer this hello builds below draws the ring
    // at that reach.
    const wardAura = h.catalog?.rules?.wardAuraPx ?? 0;
    this.fleets.wardAuraPx = wardAura;
    // The ship roster the family resolution reads (R1009). It is the server's
    // own table, and the client mirrors none of it: the role and the two
    // reaches come straight off the hello catalog.
    const ships = h.catalog?.ships;
    if (ships && ships.length > 0) {
      COMBAT_ROSTER.length = 0;
      for (const sp of ships) {
        COMBAT_ROSTER.push({
          cls: sp.id, role: sp.role, rangePx: sp.range, anchorRangePx: sp.anchorRange,
        });
      }
      this.combat.setRoster(COMBAT_ROSTER);
    }
    // A hello is a new world: nothing that was in the water belongs to it.
    this.combat.clear();
    this.gather.clear();
    this.signature.clear();
    this.monsters.clear();
    // The gather figures ride the fleet layer and not the gather layer, so
    // they are taken off beside it (R1123).
    this.fleets.clearGatherFigures();

    this.territory.applyOwners(new Uint8Array(h.owners));
    this.terrain.setBlocked(new Uint8Array(h.blocked));
    // The pocket walk reads the mask: an enclosed RIDGE is not a pocket
    // (R433). The terrain layer has just written the engine's plane, so this
    // call only says the plane has moved.
    this.territory.setBlocked();
    // The trench list the beast rises from is this world's (R525).
    this.kraken.setBlocked(new Uint8Array(h.blocked));
    // The survey of the viewer's faction (R2626): the hatch takes the
    // bitset of the hello, and no bitset hatches nothing.
    this.survey.reset(h.survey && h.survey.length > 0 ? h.survey : null);

    // The sonar's static coverage belongs to the world that is going away.
    this.sonar.reset();
    // The looks of a Sapper belong to the world that is going away (R2657).
    this.sapperWork.clear();
    // A hello replaces the whole site set, so it replaces the layer. A
    // reconnect rebuilds it, and the clan frame that named the squad does
    // not come again — hand the new layer the set.
    this.sites = new SiteLayer(engine, h.sites, h.playerId, h.faction);
    // The reach is this hello's and so is the layer: a write that stood
    // before the build reached the layer it replaced, and no ring drew (R1326).
    this.sites.wardAuraPx = wardAura;
    this.sites.labels = opts.labels !== false;
    this.sites.setStyle(this.holdSymbol, this.holdEra);
    // The look the page picked, which the layer this hello replaced held.
    this.sites.setLook(this.sceneLook);
    if (this.squadIds.size > 0) this.sites.setSquadIds(this.squadIds);
    engine.setLayer('sites', this.sites);
    // The hold feed reads `this.sites`, which the line above replaced. The
    // arrow closes over the SCENE and not over the layer, so it needs no
    // re-pointing.

    this.baseCells.clear();
    this.structureCells.clear();
    for (const s of h.sites) this.rememberSite(s);

    // The map author's marks. They never change during a match, so the layer
    // is packed here from the hello and never touched again (R303).
    if (h.arcade && h.arcade.markers.length > 0) this.markers.set(h.arcade.markers);
    else this.markers.clear();
    // The arcade structures ride the hello one time, like the marks
    // (arcade.md §19.6, R2029). A season hello carries no arcade block and
    // leaves the Sieges path alone.
    if (h.arcade) this.setStructures(h.arcade.structures ?? []);

    const [hx, hy] = cellCenter(h.homeCell);
    this.home = { x: hx, y: hy };
    // Gather and return legs draw to the home hold (R43).
    this.fleets.home = { x: hx, y: hy };
    opts.camera.centerOn(hx, hy, 0.9);
    // The furniture reads the whole world at a hello: the ridges, the
    // trenches and the name each one draws (R548). It runs after the site
    // layer, because a piece that reads a deposit reads it there.
    this.chart?.invalidate('hello');
  }

  /** The legs of the shipping lanes of the faction (the own plots with
   *  LANE and the mates' lanes), read again only on a new empire frame. */
  private factionLaneLegs(): readonly SeaLaneLeg[] {
    const st = this.opts.state;
    if (st.empire !== this.laneFrame) {
      this.laneFrame = st.empire;
      this.laneLegsV = [...st.plots.filter((p) => p.lane), ...st.factionLanes].flatMap(laneLegs);
    }
    return this.laneLegsV;
  }

  /** The shipping lanes of the faction as tracks of the chain (R3066),
   *  read again only on a new empire frame. */
  private factionLaneTracks(): readonly ChainTrack[] {
    const st = this.opts.state;
    if (st.empire !== this.trackFrame) {
      this.trackFrame = st.empire;
      this.laneTracksV = [...st.plots.filter((p) => p.lane), ...st.factionLanes].flatMap(laneTracks);
    }
    return this.laneTracksV;
  }

  /** The figures of the chain off the hello catalog (R3066), or null
   *  while the world sends none; read again only on new rules. */
  private chainFigures(): ChainFigures | null {
    const st = this.opts.state, r = st.rules.seaObjects;
    if (r !== this.chainRules) {
      this.chainRules = r;
      const reach = st.seaObject(SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT)?.reachPx ?? 0;
      this.chainFig = r && r.laneChainPx > 0 && reach > 0
        ? { reach, chain: r.laneChainPx, snap: r.laneSnapPx } : null;
    }
    return this.chainFig;
  }

  // -- the state frame -----------------------------------------------------

  /**
   * Moves the world one state frame on.
   *
   * Frames are deltas (protocol v4): a full frame replaces the picture,
   * everything else adds, updates and explicitly removes.
   */
  applyState(s: State): void {
    const { opts } = this;
    // Territory rides the state frame (v15): a full frame restates the whole
    // owners map, a delta carries the tick's flips. The resync after dropped
    // frames covers territory too, so a missed flip burst cannot leave
    // ground on screen the sea has taken.
    if (s.full && s.owners.length > 0) {
      this.territory.applyOwners(s.owners);
      opts.onOwnersReplaced?.();
      this.chart?.invalidate('owners');
    }
    // The survey rides the state frame (R2611, R2626): the store takes the
    // bitset, the points and the bands and currents of the new cells, and
    // the hatch takes the cells. A frame that changed nothing costs a
    // compare.
    const sv = opts.state.applySurvey(s);
    this.finds = sv && !sv.full ? sv.pois.filter((p) => poiShown(p.kind)) : NO_FINDS;
    if (sv) {
      this.survey.apply(sv, opts.state.survey.bits);
      this.chart?.invalidate('survey');
    }
    // The seamount names ride the state frame (R2620, R2633): the chart
    // writes the names again only when one changed.
    if (opts.state.seamounts.applyState(s)) this.chart?.invalidate('seamounts');
    // The cache list of TOGGLE CACHES (R2825): the points of interest
    // piece reads it, and it packs again on the survey reason.
    if (opts.state.applyAdminCaches(s)) this.chart?.invalidate('survey');
    // The cargo sightings of the commander (R2843): the TREASURE pill and
    // the wreck card read them at the 4 Hz of the text panels.
    opts.state.applyCargoSightings(s);
    // A ring marks a first find on the map (round 088 card 06).
    for (const p of this.finds) {
      const [px, py] = cellCenter(p.cell);
      this.fx.ring(px, py, opts.state.faction);
    }
    for (const f of s.flips) {
      this.territory.applyFlip(f.index, f.to);
      opts.onFlip?.(f.index, f.to);
      this.chart?.invalidate('flip');
      const [fx, fy] = cellCenter(f.index);
      this.fx.ring(fx, fy, f.to);
    }

    this.fleets.applyState(s.fleets, s.removed, s.full);
    this.signature.applyState(s.fleets, s.removed, s.full);
    // The monster bodies ride the latched flag `pirates` and nothing else
    // (R2719): a world without it keeps no record and draws none.
    this.monsters.setGate(opts.state.hasFeatureFlag('pirates'));
    this.monsters.applyState(s.fleets, s.removed, s.full);
    // The clock of every own guard (R2080): the frame carries it on an own
    // hull alone, so a contact never adds a record.
    opts.state.guards.observeFleets(s.fleets, s.removed, s.full, Date.now(), opts.state.tacticalScale);
    // The sea objects ride the State frame (R2654, R2657): the Sapper
    // fields first, so a collect reads the stow of this frame.
    const sea = opts.state.seaObjects, now = Date.now();
    sea.observeFleets(s.fleets, s.removed, s.full, now, opts.state.tacticalScale, opts.state.faction);
    this.sapperWork.setViewer(opts.state.faction);
    this.sapperWork.applyDelta(sea.applyState(s, now, seaHulls(s.fleets), SEA_INFERENCE), now);
    opts.onFleetsApplied?.();
    this.torpedoes.apply(s.torpedoes);

    // The beam rows are deltas (R2442): a full frame clears the picture,
    // a row upserts, a gone key drops. Nothing below runs on a frame that
    // left the picture as it stood.
    this.beamsChanged = this.beamPicture.apply(s);
    if (this.beamsChanged) {
      this.beams = this.beamPicture.list;
      this.control.apply(this.beams);
      this.contestByCell.clear();
      for (const b of this.beams) {
        this.contestByCell.set(b.targetCell, { challenger: b.faction, gauge: b.progress });
      }
    }
    if (s.full) this.gaugeByCell.clear();
    for (const g of s.gauges) this.gaugeByCell.set(g.index, g.gauge10);
    // The hulls, the beams and the contested cells have all moved, so every
    // piece that draws one of them packs again (R548).
    this.chart?.invalidate('state');
  }

  // -- the events ----------------------------------------------------------

  /** The viewer's account id, 0 before the hello. */
  private get viewerId(): number { return this.opts.state.hello?.playerId ?? 0; }

  /** The blasts, the launches and the rounds that stopped (R82, R164). */
  applyEvents(events: WireEvent[]): void {
    const { opts } = this;
    // The spends and the gun hits of the signature looks (R2408). They come
    // before the gun-shot layer's salvo, because a HIT of a tick reaches the
    // client before that tick's State frame clears the order.
    this.signature.applyEvents(events);
    for (const e of events) {
      const x = e.xMpx / MPX_PER_PX, y = e.yMpx / MPX_PER_PX;
      // A splash rides beside a primary torpedo hit, and the primary event
      // already carried the blast, the cue and the jolt. So a splash draws
      // the FIGURE and nothing else (R538): a second blast on the same
      // detonation would read as a second torpedo.
      const splash = e.kind === EventKind.EVENT_KIND_TORPEDO_HIT && e.splash;
      // The gun shots and the hits (R1007). A gun HIT flies one salvo and a
      // fleet's EXPLOSION plays the final blow; the layer answers how long the
      // damage figure must wait for the first round to land (R1010), and the
      // figure then arrives with the mark.
      // A gun hit from a monster plays the strike of its body (R2719); the
      // combat layer draws no salvo for it.
      if (e.kind === EventKind.EVENT_KIND_HIT && e.faction === 0) {
        this.monsters.struck(e.sourceXMpx / MPX_PER_PX, e.sourceYMpx / MPX_PER_PX, x, y);
      }
      const wait = this.combat.enabled ? this.fireCombat(e, x, y) : 0;
      // A gun shot at a sea object flies the salvo of a shot at a hull
      // (R3031). The store notes it, so the State frame that drops an
      // armed sea mine plays its detonation when the first round lands.
      if (e.kind === EventKind.EVENT_KIND_HIT && e.seaObject > 0) {
        opts.state.seaObjects.noteGunHit(e.seaObject, {
          x: e.sourceXMpx / MPX_PER_PX, y: e.sourceYMpx / MPX_PER_PX, waitSec: wait,
        });
      }
      this.showDamage(e, x, y, wait);
      this.flashAspect(e, wait);
      if (splash) continue;
      // Every event carries a place, so its cue is positional: the director
      // dampens it by distance and by zoom. Flips inside a state frame stay
      // silent — that channel reports them already. A sinking Beacon forks
      // to its own voice, which is why the class of the victim goes in, and
      // a combat cue sounds for the two people in the exchange alone (R551),
      // which is why the viewer's id goes in. A gun hit speaks on the first
      // hit of each side of a fight and plays the effect alone after (R629).
      // A flip, a capture, a guard's kill and a signature order fork on the
      // viewer's faction (R3118-R3120), and a flip keeps its gap.
      const cue = mapCue(e, (id) => this.fleets.classOf(id), this.viewerId, this.fight,
        opts.state.faction, this.flipGap);
      if (cue) opts.cue(cue.id, x, y, cue.scale, cue.voice);
      this.blast(e, x, y);
    }
    // The kills of one round burst sound once, at the burst point (R3118).
    for (const b of burstCues(events, this.viewerId, opts.state.faction)) {
      opts.cue(b.sfx.id, b.xMpx / MPX_PER_PX, b.yMpx / MPX_PER_PX, b.sfx.scale, b.sfx.voice);
    }
  }

  /** The mark one event leaves in the water. An unnamed kind draws a ring. */
  private blast(e: WireEvent, x: number, y: number): void {
    switch (e.kind) {
      // A gun hit and a sunk FLEET are the combat layer's now (R1007): the
      // salvo and the final blow replace the small ring and the boom. `#shots=0`
      // puts the two back, and a hold's destruction keeps the boom either way,
      // because a hold is no fleet and has no final-blow look.
      case EventKind.EVENT_KIND_EXPLOSION:
        if (!this.combat.enabled || e.victim === 0) this.fx.explosion(x, y, e.faction);
        return;
      case EventKind.EVENT_KIND_HIT:
        if (!this.combat.enabled) this.fx.hit(x, y, e.faction);
        return;
      case EventKind.EVENT_KIND_LAUNCH:
        this.fx.launch(x, y, e.faction);
        return;
      // A round that goes off draws the detonation look of the World theme,
      // out to the splash of its kind (R2079). The look and the cue are
      // unconditional: they are how a bystander sees and hears a fight. Only
      // the camera jolt belongs to the two people in it, and the server names
      // both.
      case EventKind.EVENT_KIND_TORPEDO_HIT:
        // A waiting Stonefish that goes off takes its wait marks with it
        // at once (R2848). A splash event names no round kind.
        if (!e.splash) this.torpedoes.endWait(WAIT_BOOM, e.torpedoKind, x, y);
        this.goOff(e, x, y);
        // A repair is no impact (R2783): the camera does not jolt for it.
        if (!e.heal && impactIsMine(this.viewerId, e.actor, e.subject)) this.torpedoes.impact();
        return;
      // Every round explodes (R82): fuel end and rock impact go off where
      // the flight stopped. A round a guard or a burst took out raises a
      // ROUND_KILL below instead (R2080).
      case EventKind.EVENT_KIND_TORPEDO_EXPIRE:
        this.torpedoes.endWait(WAIT_BOOM, e.torpedoKind, x, y);
        this.goOff(e, x, y);
        return;
      // A round a guard or a burst took out of the water (R2080): the snap
      // from the killer's point, then the brackets shut on the round. It
      // did not go off, so it draws no detonation and no blast.
      case EventKind.EVENT_KIND_ROUND_KILL:
        // A waiting Stonefish a guard or a burst took out fades its wait
        // marks (R2848).
        this.torpedoes.endWait(WAIT_KILL, e.torpedoKind, x, y);
        this.guard.kill(e.sourceXMpx / MPX_PER_PX, e.sourceYMpx / MPX_PER_PX, x, y,
          e.headingMrad / 1000, e.faction, e.victimFaction);
        return;
      default:
        this.fx.ring(x, y, e.faction);
    }
  }

  /**
   * One round that went off: the detonation look (R2079), and the slow ring
   * of the Torpor out to its splash (R2783), as the replay film draws it.
   * The sim slows on every detonation, a hit and a fuel end alike, so both
   * events draw the ring. A round a Sentinel or a burst took out carries
   * kind 0 and draws no ring. A Harpoon or a Remora that bursts hostile
   * rounds draws no ring: its ROUND_KILL events mark the kills.
   */
  private goOff(e: WireEvent, x: number, y: number): void {
    // A quill pod is no torpedo of a player (R2719, round 109): it bursts
    // in the NPC ink, with no fireball and no slow ring.
    if (e.torpedoKind === TorpedoKind.TORPEDO_KIND_POD) {
      this.monsters.podBurst(x, y, this.monsters.podDesignAt(x, y));
      return;
    }
    // The source of a round's event is its launch point; the wire leaves a
    // source it does not know at 0, 0, and the look then takes no course.
    const sourced = e.sourceXMpx !== 0 || e.sourceYMpx !== 0;
    this.fx.detonate(x, y, e.faction, e.torpedoKind,
      sourced ? e.sourceXMpx / MPX_PER_PX : x, sourced ? e.sourceYMpx / MPX_PER_PX : y);
    const slow = e.torpedoKind > 0
      ? slowRingPx(e.torpedoKind, this.opts.state.torpedo(e.torpedoKind)?.splash ?? 0) : 0;
    if (slow > 0) this.fx.slowRing(x, y, e.faction, slow);
  }

  /**
   * One event handed to the gun-shot layer (R1007).
   *
   * A gun HIT is a salvo. A FLEET explosion is the final blow; a hold's
   * destruction and every torpedo event keep their own marks, because a hold is
   * no fleet and a torpedo has a look system of its own (R164).
   *
   * The answer is how long the damage figure waits for the first round (R1010).
   */
  private fireCombat(e: WireEvent, x: number, y: number): number {
    // A replay film borrows the layer and feeds it the fight it plays. The map
    // under it is covered, so a salvo of the fight running now would stand on a
    // picture it does not belong to — the rule the figures follow (R538).
    if (this.combat.borrowed) return 0;
    const gun = e.kind === EventKind.EVENT_KIND_HIT;
    const sink = e.kind === EventKind.EVENT_KIND_EXPLOSION && e.victim !== 0;
    if (!gun && !sink) return 0;
    const ev = COMBAT_EVENT;
    ev.kind = gun ? 'hit' : 'sink';
    ev.victim = e.victim;
    ev.x = x; ev.y = y;
    ev.sourceX = e.sourceXMpx / MPX_PER_PX;
    ev.sourceY = e.sourceYMpx / MPX_PER_PX;
    ev.faction = e.faction;
    ev.victimFaction = e.victimFaction;
    ev.actor = e.actor;
    // The viewer's own reading of the victim's shields. A contact the client
    // cannot read answers -1, and the layer then takes the hull look. The
    // read allocates nothing, where `describe` builds a whole object.
    ev.shieldPct = e.victim === 0 ? -1 : this.fleets.shieldOf(e.victim);
    return this.combat.fire(ev);
  }

  /**
   * The aspect chevrons of the struck arc flash when the first round of
   * the salvo lands (R2407, §6.3.3). The fleet layer draws the flash,
   * because it replaces the rest chevrons of that arc and scales with the
   * hull mark. A film that borrows the combat layer plays a fight the map
   * under it does not hold, so the live hulls flash nothing then.
   */
  private flashAspect(e: WireEvent, wait: number): void {
    if (e.kind !== EventKind.EVENT_KIND_HIT || e.victim === 0 || this.combat.borrowed) return;
    this.fleets.flashAspect(e.victim, e.aspect,
      e.sourceXMpx / MPX_PER_PX, e.sourceYMpx / MPX_PER_PX, wait);
  }

  /**
   * The damage figure of one event, when the commander is in the exchange
   * (R538, mockup round thirty).
   *
   * A gun HIT is one shot: one event per victim per shot, carrying the
   * whole damage of that shot (R1135). The shots of several guns on one
   * hull merge into one figure inside `mergeSec` (R1136), and a torpedo hit
   * carries its own figure and one more event per splash victim. An event with no amount — a flip, a launch, an expiry —
   * draws nothing, and so does a fight between two other empires.
   *
   * The figure pins to the STRUCK HULL and not to the event's own point,
   * because the event is reported where the shot was fired and the hull
   * moves on while the rounds are in the water.
   */
  private showDamage(e: WireEvent, x: number, y: number, wait = 0): void {
    if (e.damageX10 <= 0 || this.opts.labels === false) return;
    // A replay film borrows the layer and feeds it the figures of the fight
    // it plays (R538). The map under it is covered, so a figure of the
    // fight running now would stand on a picture it does not belong to.
    if (this.damage.borrowed) return;
    if (e.kind !== EventKind.EVENT_KIND_HIT
      && e.kind !== EventKind.EVENT_KIND_TORPEDO_HIT) return;
    // A shot at a sea object struck no hull, so no figure shows (R3031).
    if (e.seaObject > 0) return;
    // One `describe` answers the side, the faction and the place: it builds
    // an object, and it is the one lookup of the hull this figure takes.
    const seen = e.victim === 0 ? null : this.fleets.describe(e.victim);
    const side = classifyDamage(this.viewerId, e.actor, e.subject, seen?.own ?? null);
    if (!side) return;
    this.damage.show({
      x: seen ? seen.x : x,
      y: seen ? seen.y : y,
      victim: e.victim,
      amount: e.damageX10 / 10,
      taken: side === 'taken',
      torpedo: e.kind === EventKind.EVENT_KIND_TORPEDO_HIT,
      splash: e.splash,
      // The wire names the struck faction (R538).
      faction: e.victimFaction,
      sourceX: e.sourceXMpx / MPX_PER_PX,
      sourceY: e.sourceYMpx / MPX_PER_PX,
      // The figure waits for the round that carries it (R1010).
      waitSec: wait,
      // Shots of several shooters on one hull make one figure (R1129).
      merge: e.kind === EventKind.EVENT_KIND_HIT,
      // The arc and the ambush of a primary gun shot (R2407, §6.3.2).
      aspect: e.aspect,
      ambush: e.ambush,
      // A Wrasse repair reads `+N` in green (R2783).
      heal: e.heal,
    });
  }

  // -- the sites, the sieges and the squad ---------------------------------

  /** A site changed hands, or its stock moved. `effect` is the owner's
   *  hold effect, 0 for every site that is not a player base (R910). */
  setSiteOwner(
    siteId: number, faction: number, amount: number, capacity: number, effect = 0,
  ): void {
    this.sites?.setOwner(siteId, faction, amount, capacity, effect);
    // An arcade structure that changed hands moves its Ward aura (R2029).
    if (this.structureIds.has(siteId)) this.syncArcadeWards();
    this.chart?.invalidate('sites');
  }

  /** A site appeared. */
  addSite(site: Site): void {
    this.sites?.add(site);
    this.rememberSite(site);
    this.chart?.invalidate('sites');
  }

  /**
   * A site is gone (R204).
   *
   * The seaquake's stage 2 kills every deposit, and the site is gone rather
   * than worked out: every layer and every cache that holds it by id must
   * let it go in the same frame. The caller resolves what was there, because
   * the state store is the page's and this layer set is only one of two
   * places that hold it.
   */
  removeSite(siteId: number, gone: Site | undefined): void {
    this.sites?.remove(siteId);
    if (gone) this.forgetSite(gone);
    this.chart?.invalidate('sites');
  }

  /** What the site layer holds under that id, for the caller's lookup. */
  site(siteId: number): Site | undefined { return this.sites?.get(siteId); }

  /**
   * What every strategic object is, rebuilt in place on each Sieges frame
   * (R1104). It is reused, so a frame allocates nothing (R131).
   */
  private readonly objects: StructureState[] = [];

  // -- the arcade structures (arcade.md §19, R2029) ------------------------

  /** The structures of an arcade map, from its hello, in cell order. */
  private arcadeStructures: readonly ArcadeStructure[] = [];

  /** The site ids of those structures, so an owner frame knows its own. */
  private readonly structureIds = new Set<number>();

  /**
   * One capture ring a capturable structure, in the order of
   * `ArcadePhase.captures`. The match writes the taker and the count in
   * place on every frame; the site layer draws a ring whose taker is set.
   */
  readonly captureMarks: CaptureMark[] = [];

  /** The site ids of the objective structures, which wear the objective
   *  tell of the site layer (R2029). */
  private readonly objectiveIds: number[] = [];

  /** The arcade Wards the two layers draw, rebuilt on an owner change. */
  private arcadeWards: WardAura[] = [];

  /**
   * Names the arcade structures of the map (R2029): the kind of each
   * control point, which an arcade server states in the hello and never in
   * a Sieges frame, the capture ring of each capturable one, and the aura
   * of each Ward that serves a team, and the objective tell of each
   * objective. A structure is never contested in the siege sense, so the
   * contested tell never draws.
   */
  setStructures(list: readonly ArcadeStructure[]): void {
    this.arcadeStructures = list;
    this.structureIds.clear();
    this.objects.length = 0;
    this.captureMarks.length = 0;
    this.objectiveIds.length = 0;
    for (const st of list) {
      this.structureIds.add(st.siteId);
      this.objects.push({ siteId: st.siteId, kind: st.kind, contested: false, attacker: 0 });
      if (st.objective) this.objectiveIds.push(st.siteId);
      if (!st.capturable) continue;
      const [x, y] = cellCenter(st.cell);
      this.captureMarks.push({
        siteId: st.siteId, x, y, edges: Float64Array.from(ringOneEdges(st.cell)), taker: 0, leftWallSec: 0,
      });
    }
    this.sites?.setObjects(this.objects);
    this.sites?.setCaptures(this.captureMarks);
    this.sites?.setObjectives(this.objectiveIds);
    this.syncArcadeWards();
  }

  /** The one point `siteAt` answers in. The guard layer reads it inside
   *  the frame and keeps nothing (R131). */
  private readonly sitePt = { x: 0, y: 0 };

  /** The centre of an arcade structure's cell, or null for a site that is
   *  no structure of the match: where a guard structure stands (R2080).
   *  The guard layer asks it on every frame, so it allocates nothing. */
  private siteAt(siteId: number): { x: number; y: number } | null {
    const list = this.arcadeStructures;
    for (let i = 0; i < list.length; i++) {
      if (list[i].siteId !== siteId) continue;
      this.sitePt.x = cellCenterX(list[i].cell);
      this.sitePt.y = cellCenterY(list[i].cell);
      return this.sitePt;
    }
    return null;
  }

  /**
   * The aura of every arcade Ward that serves a team (arcade.md §19.5): an
   * owned Ward in its owner's ink, and a public Ward, neutral and not
   * capturable, in the neutral grey for every team. A neutral Ward that
   * waits for a taker mends nothing and draws no ring.
   */
  private syncArcadeWards(): void {
    const wards: WardAura[] = [];
    for (const st of this.arcadeStructures) {
      if (st.kind !== ObjKind.OBJ_KIND_WARD) continue;
      const owner = this.sites?.get(st.siteId)?.faction ?? 0;
      const pub = owner === 0 && !st.capturable;
      if (owner === 0 && !pub) continue;
      const [x, y] = cellCenter(st.cell);
      wards.push(pub ? { x, y, faction: 0, public: true } : { x, y, faction: owner });
    }
    if (wards.length === 0 && this.arcadeWards.length === 0) return;
    this.arcadeWards = wards;
    this.sites?.setWards(wards);
    this.fleets.setWards(wards);
  }

  /** The contested cells the control layer marks. */
  setSieges(list: SiegeInfo[]): void {
    this.control.setSieges(list.filter((g) => g.contested)
      .map((g) => ({ cell: g.cell, faction: g.owner })));
    // The Sieges frame is where the client learns each object's KIND
    // (R1082). A Site record carries the site kind alone, so the held
    // Coral Wards are named here and handed to the two layers that draw
    // what one does: the ring on the water, and the plus on a hull.
    const wards: WardAura[] = [];
    for (const g of list) {
      if (g.kind !== ObjKind.OBJ_KIND_WARD || g.owner === 0) continue;
      const [x, y] = cellCenter(g.cell);
      wards.push({ x, y, faction: g.owner });
    }
    this.sites?.setWards(wards);
    this.fleets.setWards(wards);
    // The same frame names every object's KIND, its contested flag and the
    // faction that pledged (R1104). The site layer keeps the three and keys
    // the control point's mark from them.
    this.objects.length = 0;
    for (const g of list) {
      this.objects.push({
        siteId: g.siteId, kind: g.kind,
        contested: g.contested, attacker: g.attacker,
      });
    }
    this.sites?.setObjects(this.objects);
    this.chart?.invalidate('sieges');
  }

  /**
   * The viewer's squad (R199).
   *
   * Both map layers mark a squadmate's things — a hollow hull, a plate with
   * no rim (R185) — so both read the same set, and so does the next site
   * layer a hello builds.
   *
   * `slots` is the squad slot of each mate, which a friendly mark that
   * prints a numeral reads. It is optional, because a page that draws no
   * such mark has no slot to give.
   */
  setSquadIds(ids: Set<number>, slots?: ReadonlyMap<number, number>): void {
    this.squadIds = ids;
    this.fleets.setSquadIds(ids, slots);
    this.sites?.setSquadIds(ids);
    this.chart?.invalidate('clan');
  }

  /**
   * Lifts the hulls of the reader's new squad toward white, one time
   * (R1571, mockup round 075 card 18). The feed calls it after
   * `setSquadIds` when a clan frame moved the reader to another squad.
   */
  liftSquad(): void {
    this.fleets.liftSquad();
  }

  /**
   * What an arrival of the spawn layer plays over: the fades of the
   * territory, the sites and the fleets, the hold of the territory walks,
   * and the camera scale. Each effect layer rides the fade of what it is
   * drawn on (R914). `home` is the page's own: where the hold stands on
   * the screen.
   */
  spawnHost(home: () => { x: number; y: number }): FastSpawnScene {
    const view = this.engine.view;
    return {
      home,
      scale: () => view.scale.x,
      setTerritoryAlpha: (a) => this.territory.setAlpha(a),
      setSitesAlpha: (a) => {
        this.sites?.setAlpha(a);
        this.holdEffects.setAlpha(a);
      },
      setFleetsAlpha: (a) => {
        this.fleets.setAlpha(a);
        this.effects.setAlpha(a);
      },
      deferTerritory: (on) => this.territory.setDeferred(on),
    };
  }

  // -- life ----------------------------------------------------------------

  /**
   * Drops every slot the scene filled back to a `NullLayer`.
   *
   * `engine.setLayer` disposes what stood in the slot, so the world passes
   * and the mesh buffers of the terrain and the territory go with it. The
   * instance batches belong to the engine and are freed by `engine.dispose`;
   * a page that plays several matches on one engine therefore keeps a
   * handful of empty buffers per match, which is the price of not tearing
   * the context down between them.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    // Only OUR hook: a page that already built the next scene must keep its
    // grid callback when this one goes.
    if (this.engine.onGrid === this.gridHook) this.engine.onGrid = null;
    // The furniture's own listeners and its DOM pieces. The two layers go
    // with the slot walk below, as every other layer does.
    this.chart?.dispose();
    this.dropMarkerRepack();
    for (const slot of SCENE_SLOTS) this.engine.setLayer(slot, new NullLayer(slot));
    this.sites = null;
  }

  // -- the two cell sets ---------------------------------------------------

  /** The cell set a site of that kind belongs to, or null for any other kind. */
  private cellSetOf(s: Site): Set<number> | null {
    if (s.kind === SiteKind.SITE_KIND_BASE || s.kind === SiteKind.SITE_KIND_PLAYER_BASE) {
      return this.baseCells;
    }
    return s.kind === SiteKind.SITE_KIND_CONTROL_POINT ? this.structureCells : null;
  }

  private rememberSite(s: Site): void { this.cellSetOf(s)?.add(s.cell); }

  private forgetSite(s: Site): void { this.cellSetOf(s)?.delete(s.cell); }
}
